import React, { FC, useEffect, useState } from 'react';
import type {
  OrganisationCardPolicy,
  UpdateOrganisationCardPolicyRequest,
} from '@dreamai/types';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as CardIcon } from '../../icons/card-add.svg';
import { parseOptionalMoney } from './organisationCardLabels';
import {
  OrganisationPaymentCard,
  cardBrandIcon,
  cardShortLabel,
} from './organisationPaymentCards';
import OrganisationToggle from './OrganisationToggle';

interface OrganisationCardPolicyFormProps {
  policy: OrganisationCardPolicy;
  /** Saving the policy */
  busy: boolean;
  /** Saved cards; the active one is charged for member purchases */
  cards: OrganisationPaymentCard[];
  activeCardId: string | null;
  /** Changing or adding a card */
  cardsBusy: boolean;
  onSetActiveCard: (cardId: string) => Promise<boolean>;
  onAddCard: () => void;
  onSave: (patch: UpdateOrganisationCardPolicyRequest) => Promise<boolean>;
  onAdjustMembers: () => void;
}

/**
 * "Team cards": the saved cards members' purchases are charged to, whether
 * members may use them at all, and the organisation-wide caps. Caps are in
 * the policy currency; purchases in other currencies are converted. Until a
 * card is saved there is nothing to set limits on, so only the add-card row
 * shows. Per-member caps live in OrganisationCardMemberLimitsModal.
 */
const OrganisationCardPolicyForm: FC<OrganisationCardPolicyFormProps> = ({
  policy,
  busy,
  cards,
  activeCardId,
  cardsBusy,
  onSetActiveCard,
  onAddCard,
  onSave,
  onAdjustMembers,
}) => {
  const [enabled, setEnabled] = useState(policy.cardEnabled);
  const [perTransaction, setPerTransaction] = useState(
    policy.perTransactionCap === null ? '' : String(policy.perTransactionCap),
  );
  const [cycle, setCycle] = useState(
    policy.cycleCap === null ? '' : String(policy.cycleCap),
  );
  const [error, setError] = useState<string | null>(null);
  const currency = policy.currency;

  useEffect(() => {
    setEnabled(policy.cardEnabled);
    setPerTransaction(
      policy.perTransactionCap === null ? '' : String(policy.perTransactionCap),
    );
    setCycle(policy.cycleCap === null ? '' : String(policy.cycleCap));
  }, [policy]);

  const perTransactionValue = parseOptionalMoney(perTransaction);
  const cycleValue = parseOptionalMoney(cycle);

  const dirty = enabled
    ? enabled !== policy.cardEnabled ||
      perTransactionValue !== policy.perTransactionCap ||
      cycleValue !== policy.cycleCap
    : enabled !== policy.cardEnabled;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!enabled) {
      await onSave({
        cardEnabled: false,
        perTransactionCap: policy.perTransactionCap,
        cycleCap: policy.cycleCap,
      });
      return;
    }
    if (perTransactionValue === undefined || cycleValue === undefined) {
      setError('Limits must be amounts of zero or more, or left blank.');
      return;
    }
    await onSave({
      cardEnabled: true,
      perTransactionCap: perTransactionValue,
      cycleCap: cycleValue,
    });
  };

  const addCardRow = (label: string) => (
    <button
      type='button'
      className='organisation-team-card-row organisation-team-card-add'
      disabled={cardsBusy}
      onClick={onAddCard}
    >
      <CardIcon className='organisation-team-card-icon' aria-hidden='true' />
      <span className='organisation-team-card-label'>
        {cardsBusy ? 'Opening…' : label}
      </span>
      <PlusIcon className='organisation-team-card-action' aria-hidden='true' />
    </button>
  );

  return (
    <form
      className='organisation-card'
      data-testid='organisation-card-policy'
      onSubmit={handleSubmit}
    >
      <h3 className='organisation-card-title'>Team cards</h3>
      <p className='organisation-card-text'>
        Add a shared payment method for your invited members to use on
        purchases.
      </p>

      {cards.length === 0 ? (
        addCardRow(
          'Add a Team Card. You’ll be able to set limits and permissions once it’s saved',
        )
      ) : (
        <>
          <div className='organisation-team-cards'>
            <h4 className='organisation-subcard-title'>
              {cards.length === 1 ? 'Your saved card' : 'Your saved cards'}
            </h4>
            <ul className='organisation-team-card-list'>
              {cards.map((card) => {
                const active = card.id === activeCardId;
                return (
                  <li
                    key={card.id}
                    className='organisation-team-card-row'
                    data-testid={`organisation-team-card-${card.id}`}
                  >
                    <img
                      className='organisation-team-card-icon'
                      src={cardBrandIcon(card)}
                      alt=''
                    />
                    <span className='organisation-team-card-label'>
                      {cardShortLabel(card)}
                    </span>
                    {active && cards.length > 1 && (
                      <span className='organisation-chip active'>Active</span>
                    )}
                    {!active && (
                      <button
                        type='button'
                        className='btn-link organisation-team-card-action'
                        disabled={cardsBusy}
                        onClick={() => onSetActiveCard(card.id)}
                      >
                        Set as active card
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            {addCardRow('Add another Card')}
          </div>

          <div className='organisation-team-spend'>
            <h4 className='organisation-subcard-title'>Manage your spend</h4>
            <div className='organisation-team-row'>
              <span className='organisation-team-row-label'>
                Allow team members to make purchases with my saved cards
              </span>
              <OrganisationToggle
                on={enabled}
                disabled={busy}
                label='Allow team members to make purchases with my saved cards'
                onChange={setEnabled}
              />
            </div>

            {enabled && (
              <>
                <div className='organisation-team-row'>
                  <span className='organisation-team-row-label'>
                    Set limits ({currency})
                  </span>
                  <div className='organisation-team-limits'>
                    <label className='organisation-team-limit'>
                      <span className='field-label'>Per purchase</span>
                      <input
                        type='number'
                        className='field-input'
                        min={0}
                        step='0.01'
                        placeholder='No limit'
                        value={perTransaction}
                        disabled={busy}
                        onChange={(e) => setPerTransaction(e.target.value)}
                      />
                    </label>
                    <label className='organisation-team-limit'>
                      <span className='field-label'>Per month</span>
                      <input
                        type='number'
                        className='field-input'
                        min={0}
                        step='0.01'
                        placeholder='No limit'
                        value={cycle}
                        disabled={busy}
                        onChange={(e) => setCycle(e.target.value)}
                      />
                    </label>
                  </div>
                </div>
                <button
                  type='button'
                  className='organisation-team-row organisation-team-row-button'
                  disabled={busy}
                  onClick={onAdjustMembers}
                >
                  <span className='organisation-team-row-label'>
                    Adjust spending limits for specific members
                  </span>
                  <ArrowRightIcon
                    className='organisation-team-card-action'
                    aria-hidden='true'
                  />
                </button>
              </>
            )}

            {error && (
              <p className='organisation-feedback error' role='alert'>
                {error}
              </p>
            )}

            <div className='organisation-actions'>
              <button
                type='submit'
                className='btn-primary'
                disabled={busy || !dirty}
              >
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </>
      )}
    </form>
  );
};

export default OrganisationCardPolicyForm;
